/**
 * The thinking games: 2048, Memory, Démineur and Simon.
 *
 * These are built from DOM elements rather than a canvas. They are grids of
 * discrete things that need focus, labels and hit targets a thumb can find, and
 * reimplementing that on a canvas would mean reimplementing buttons badly.
 */
import { byLevel } from '@arena/games';

import { Lifecycle, dpad, h, shuffle, tone } from '../kit.js';
import type { GameDef } from '../types.js';

function pill(label: string, value: string): [HTMLElement, HTMLElement] {
  const box = h('div', 'ac-pill');
  const strong = h('strong', '', value);
  box.append(h('span', '', label), strong);
  return [box, strong];
}

// ---------------------------------------------------------------------- 2048

const TILE_COLOURS: Record<number, string> = {
  2: '#2a2140',
  4: '#33265a',
  8: '#4c2a7a',
  16: '#6b2f93',
  32: '#8b5cf6',
  64: '#a855f7',
  128: '#c026d3',
  256: '#db2777',
  512: '#ec4899',
  1024: '#f43f5e',
  2048: '#facc15',
};

export const g2048: GameDef = {
  id: 'two-zero-four-eight',
  title: '2048',
  blurb: 'Fusionne les tuiles pour atteindre 2048.',
  category: 'Réflexion',
  difficulty: 'Difficile',
  icon: '\u{1F9E9}',
  unit: 'points',
  higherIsBetter: true,
  how: 'Fleches, ZQSD ou glisse. Deux tuiles identiques qui se rencontrent fusionnent.',
  mount(root, ctx) {
    const life = new Lifecycle();
    /*
     * Difficulty is the size of the board and how often a 4 turns up.
     *
     * A fifth column and row is an enormous kindness: the game is lost when the
     * board fills, and 25 squares take far longer to fill than 16 while every
     * merge still works the same way. Hard mode keeps the classic 4x4 and
     * seeds far more 4s, which clogs the board with tiles that cannot pair with
     * the 2s around them.
     */
    const N = byLevel(ctx.level, 5, 4, 4);
    const FOUR_CHANCE = byLevel(ctx.level, 0.08, 0.1, 0.34);
    const [scoreBox, scoreValue] = pill('Score', '0');
    const [bestBox, bestValue] = pill('Max', '2');
    const hud = h('div', 'ac-hud');
    hud.append(scoreBox, bestBox);

    const board = h('div', 'ac-board');
    board.style.gridTemplateColumns = `repeat(${N}, 1fr)`;
    // Without this the browser claims the gesture for scrolling and the swipe
    // never arrives, which on a tablet reads as the game being broken.
    board.style.touchAction = 'none';
    const cells: HTMLElement[] = [];
    for (let i = 0; i < N * N; i++) {
      const c = h('div', 'ac-cell');
      c.style.cursor = 'default';
      cells.push(c);
      board.append(c);
    }

    let grid: number[] = new Array(N * N).fill(0);
    let score = 0;
    let over = false;

    const at = (r: number, c: number): number => grid[r * N + c];
    const set = (r: number, c: number, v: number): void => {
      grid[r * N + c] = v;
    };

    const addTile = (): void => {
      const empty: number[] = [];
      grid.forEach((v, i) => {
        if (v === 0) empty.push(i);
      });
      if (!empty.length) return;
      grid[empty[Math.floor(Math.random() * empty.length)]] = Math.random() < FOUR_CHANCE ? 4 : 2;
    };

    const paint = (): void => {
      grid.forEach((v, i) => {
        const cell = cells[i];
        cell.textContent = v ? String(v) : '';
        cell.style.background = v ? (TILE_COLOURS[v] ?? '#facc15') : '#170f28';
        cell.style.color = v >= 1024 ? '#1a1030' : '#f4f0ff';
        cell.style.fontSize = v >= 1024 ? 'clamp(13px,3.6vw,22px)' : 'clamp(16px,5vw,30px)';
      });
      scoreValue.textContent = String(score);
      bestValue.textContent = String(Math.max(...grid, 2));
    };

    /** Collapse one line toward index 0. Returns the line and points scored. */
    const collapse = (line: number[]): { line: number[]; gained: number } => {
      const kept = line.filter((v) => v !== 0);
      const out: number[] = [];
      let gained = 0;
      for (let i = 0; i < kept.length; i++) {
        // A tile that has just merged cannot merge again in the same move, which
        // is why this skips ahead rather than looping over the output.
        if (i + 1 < kept.length && kept[i] === kept[i + 1]) {
          out.push(kept[i] * 2);
          gained += kept[i] * 2;
          i++;
        } else {
          out.push(kept[i]);
        }
      }
      while (out.length < N) out.push(0);
      return { line: out, gained };
    };

    const move = (dx: number, dy: number): void => {
      if (over) return;
      const before = grid.join(',');
      let gained = 0;

      for (let i = 0; i < N; i++) {
        const line: number[] = [];
        for (let j = 0; j < N; j++) {
          // Read each row or column in the direction of travel, so one collapse
          // routine serves all four moves.
          const r = dy !== 0 ? (dy > 0 ? N - 1 - j : j) : i;
          const c = dx !== 0 ? (dx > 0 ? N - 1 - j : j) : i;
          line.push(at(r, c));
        }
        const res = collapse(line);
        gained += res.gained;
        res.line.forEach((v, j) => {
          const r = dy !== 0 ? (dy > 0 ? N - 1 - j : j) : i;
          const c = dx !== 0 ? (dx > 0 ? N - 1 - j : j) : i;
          set(r, c, v);
        });
      }

      if (grid.join(',') === before) return; // nothing moved, so no new tile
      score += gained;
      if (gained) tone(400 + Math.min(600, gained), 55, 'triangle');
      addTile();
      paint();

      // Game over only when no move in any direction would change anything.
      const stuck = !([[1, 0], [-1, 0], [0, 1], [0, -1]] as const).some(([tx, ty]) => {
        const snapshot = grid.slice();
        const savedScore = score;
        move2(tx, ty);
        const changed = grid.join(',') !== snapshot.join(',');
        grid = snapshot;
        score = savedScore;
        return changed;
      });
      if (stuck) {
        over = true;
        ctx.submit(score);
        note.textContent = `Bloque. ${score} points, meilleure tuile ${Math.max(...grid)}.`;
      }
    };

    /** The same transform without side effects, used only to test for deadlock. */
    const move2 = (dx: number, dy: number): void => {
      for (let i = 0; i < N; i++) {
        const line: number[] = [];
        for (let j = 0; j < N; j++) {
          const r = dy !== 0 ? (dy > 0 ? N - 1 - j : j) : i;
          const c = dx !== 0 ? (dx > 0 ? N - 1 - j : j) : i;
          line.push(at(r, c));
        }
        collapse(line).line.forEach((v, j) => {
          const r = dy !== 0 ? (dy > 0 ? N - 1 - j : j) : i;
          const c = dx !== 0 ? (dx > 0 ? N - 1 - j : j) : i;
          set(r, c, v);
        });
      }
    };

    const note = h('p', 'ac-note', 'Glisse, touche les fleches, ou joue au clavier.');
    const pad = dpad(life, move);

    life.on(window, 'keydown', (ev: KeyboardEvent) => {
      const k = ev.key.toLowerCase();
      if (k === 'arrowleft' || k === 'q' || k === 'a') move(-1, 0);
      else if (k === 'arrowright' || k === 'd') move(1, 0);
      else if (k === 'arrowup' || k === 'z' || k === 'w') move(0, -1);
      else if (k === 'arrowdown' || k === 's') move(0, 1);
      else return;
      ev.preventDefault();
    });

    let start: { x: number; y: number } | null = null;
    life.on(board, 'pointerdown', (ev: Event) => {
      const e = ev as PointerEvent;
      start = { x: e.clientX, y: e.clientY };
    });
    life.on(board, 'pointerup', (ev: Event) => {
      const e = ev as PointerEvent;
      if (!start) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      start = null;
      if (Math.hypot(dx, dy) < 24) return;
      if (Math.abs(dx) > Math.abs(dy)) move(dx > 0 ? 1 : -1, 0);
      else move(0, dy > 0 ? 1 : -1);
    });

    const again = h('button', 'ac-ghost', 'Nouvelle partie');
    again.addEventListener('click', () => {
      grid = new Array(N * N).fill(0);
      score = 0;
      over = false;
      addTile();
      addTile();
      paint();
      note.textContent = 'Glisse, touche les fleches, ou joue au clavier.';
    });

    addTile();
    addTile();
    paint();
    root.append(hud, board, pad, note, again);
    return () => life.stop();
  },
};

// -------------------------------------------------------------------- memory

export const memory: GameDef = {
  id: 'memory',
  title: 'Memory',
  blurb: 'Retrouve les paires en un minimum de coups.',
  category: 'Réflexion',
  difficulty: 'Facile',
  icon: '\u{1F9E0}',
  unit: 'coups',
  higherIsBetter: false,
  how: 'Retourne deux cartes. Si elles sont identiques elles restent. Le score est le nombre de coups, donc moins il y en a mieux c’est.',
  mount(root, ctx) {
    const life = new Lifecycle();
    const ALL_ICONS = [
      '\u{1F680}', '\u{1F3AF}', '\u{1F438}', '\u{1F3B2}', '\u{1F34E}', '\u{1F511}',
      '\u{1F3B8}', '\u{1F320}', '\u{1F955}', '\u{1F3A9}', '\u{1F41D}', '\u{1F52D}',
    ];
    /*
     * Difficulty is how many pairs there are, which is the only honest knob
     * here: everything else about Memory is your own head.
     *
     * The score is moves rather than time, so a bigger board is not merely a
     * longer version of the same game. Twelve pairs is roughly twice the
     * working memory of six, and each difficulty keeps its own board, so the
     * counts never have to be comparable.
     */
    const PAIRS = byLevel(ctx.level, 6, 8, 12);
    const COLUMNS = byLevel(ctx.level, 4, 4, 6);
    const ICONS = ALL_ICONS.slice(0, PAIRS);

    const [movesBox, movesValue] = pill('Coups', '0');
    const [pairsBox, pairsValue] = pill('Paires', `0 / ${PAIRS}`);
    const hud = h('div', 'ac-hud');
    hud.append(movesBox, pairsBox);

    const board = h('div', 'ac-board');
    board.style.gridTemplateColumns = `repeat(${COLUMNS}, 1fr)`;

    let deck: string[] = [];
    let flipped: number[] = [];
    let matched = new Set<number>();
    let moves = 0;
    let busy = false;
    const cards: HTMLButtonElement[] = [];

    const paint = (): void => {
      cards.forEach((card, i) => {
        const shown = flipped.includes(i) || matched.has(i);
        card.textContent = shown ? deck[i] : '';
        card.style.background = matched.has(i)
          ? 'rgba(163,230,53,0.18)'
          : shown
            ? '#33265a'
            : '#170f28';
        card.style.borderColor = matched.has(i) ? '#a3e635' : '#2a1c47';
      });
      movesValue.textContent = String(moves);
      pairsValue.textContent = `${matched.size / 2} / ${PAIRS}`;
    };

    const deal = (): void => {
      deck = shuffle([...ICONS, ...ICONS]);
      flipped = [];
      matched = new Set();
      moves = 0;
      busy = false;
      paint();
    };

    for (let i = 0; i < PAIRS * 2; i++) {
      const card = h('button', 'ac-cell') as HTMLButtonElement;
      card.addEventListener('click', () => {
        if (busy || matched.has(i) || flipped.includes(i)) return;
        flipped.push(i);
        tone(520, 35, 'sine', 0.03);
        paint();
        if (flipped.length < 2) return;

        moves++;
        const [a, b] = flipped;
        if (deck[a] === deck[b]) {
          matched.add(a);
          matched.add(b);
          flipped = [];
          tone(780, 80, 'triangle');
          paint();
          if (matched.size === 16) {
            ctx.submit(moves);
            note.textContent = `Termine en ${moves} coups.`;
          }
          return;
        }
        // Hold the mismatch on screen long enough to actually be memorised.
        busy = true;
        paint();
        life.timeout(() => {
          flipped = [];
          busy = false;
          paint();
        }, 750);
      });
      cards.push(card);
      board.append(card);
    }

    const note = h('p', 'ac-note', 'Huit paires. Le moins de coups possible.');
    const again = h('button', 'ac-ghost', 'Nouvelle partie');
    again.addEventListener('click', () => {
      deal();
      note.textContent = 'Huit paires. Le moins de coups possible.';
    });

    deal();
    root.append(hud, board, note, again);
    return () => life.stop();
  },
};

// ------------------------------------------------------------------ demineur

export const minesweeper: GameDef = {
  id: 'minesweeper',
  title: 'Démineur',
  blurb: 'Decouvre la grille sans toucher une mine.',
  category: 'Réflexion',
  difficulty: 'Difficile',
  icon: '\u{1F4A3}',
  unit: 's',
  higherIsBetter: false,
  how: 'Clic pour ouvrir, clic droit ou appui long pour poser un drapeau. Le score est le temps mis pour finir.',
  mount(root, ctx) {
    const life = new Lifecycle();
    /*
     * Difficulty is the grid and the mine density.
     *
     * Density is what actually decides whether the board can be reasoned out or
     * has to be guessed: 6 in 49 is about 12%, the classic 10 in 81 is 12.3%,
     * and 22 in 121 is 18%, which is where forced coin flips start appearing.
     * The score is the clock, and a bigger grid takes longer whatever you do,
     * which is another reason each difficulty keeps its own board.
     */
    const N = byLevel(ctx.level, 7, 9, 11);
    const MINES = byLevel(ctx.level, 6, 10, 22);

    const [flagBox, flagValue] = pill('Drapeaux', `0 / ${MINES}`);
    const [timeBox, timeValue] = pill('Temps', '0');
    const hud = h('div', 'ac-hud');
    hud.append(flagBox, timeBox);

    const board = h('div', 'ac-board');
    board.style.gridTemplateColumns = `repeat(${N}, 1fr)`;
    board.style.maxWidth = '420px';
    board.style.gap = '4px';

    let mines = new Set<number>();
    let open = new Set<number>();
    let flags = new Set<number>();
    let started = 0;
    let done = false;
    const cells: HTMLButtonElement[] = [];

    const neighbours = (i: number): number[] => {
      const r = Math.floor(i / N);
      const c = i % N;
      const out: number[] = [];
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          if (!dr && !dc) continue;
          const nr = r + dr;
          const nc = c + dc;
          if (nr < 0 || nc < 0 || nr >= N || nc >= N) continue;
          out.push(nr * N + nc);
        }
      }
      return out;
    };

    const count = (i: number): number => neighbours(i).filter((n) => mines.has(n)).length;

    /**
     * Mines are laid AFTER the first click, avoiding it and its neighbours.
     *
     * Otherwise the first click is a coin toss that can end the game before it
     * starts, which is the single most annoying thing about naive versions.
     */
    const layMines = (safe: number): void => {
      const forbidden = new Set([safe, ...neighbours(safe)]);
      const pool = shuffle(
        Array.from({ length: N * N }, (_, i) => i).filter((i) => !forbidden.has(i)),
      );
      mines = new Set(pool.slice(0, MINES));
    };

    const paint = (): void => {
      cells.forEach((cell, i) => {
        if (open.has(i)) {
          const n = count(i);
          cell.textContent = mines.has(i) ? '\u{1F4A3}' : n ? String(n) : '';
          cell.style.background = mines.has(i) ? 'rgba(239,68,68,0.35)' : '#241539';
          cell.style.color = ['#f4f0ff', '#22d3ee', '#a3e635', '#facc15', '#fb923c', '#ff2d95'][
            Math.min(5, n)
          ];
        } else {
          cell.textContent = flags.has(i) ? '\u{1F6A9}' : '';
          cell.style.background = '#170f28';
          cell.style.color = '#f4f0ff';
        }
        cell.style.fontSize = 'clamp(12px,3.4vw,18px)';
      });
      flagValue.textContent = `${flags.size} / ${MINES}`;
    };

    const reveal = (i: number): void => {
      if (open.has(i) || flags.has(i)) return;
      open.add(i);
      // Empty cells cascade, which is the whole reason the game is playable.
      if (count(i) === 0 && !mines.has(i)) {
        for (const n of neighbours(i)) reveal(n);
      }
    };

    const finish = (won: boolean): void => {
      done = true;
      if (won) {
        const secs = Math.round((performance.now() - started) / 1000);
        ctx.submit(secs);
        note.textContent = `Gagne en ${secs} secondes.`;
        tone(880, 140, 'triangle');
      } else {
        for (const m of mines) open.add(m);
        note.textContent = 'Perdu. Recommence.';
        tone(140, 300, 'square');
      }
      paint();
    };

    const clickCell = (i: number): void => {
      if (done) return;
      if (!started) {
        layMines(i);
        started = performance.now();
      }
      if (flags.has(i)) return;
      if (mines.has(i)) {
        open.add(i);
        finish(false);
        return;
      }
      reveal(i);
      paint();
      if (open.size === N * N - MINES) finish(true);
    };

    for (let i = 0; i < N * N; i++) {
      const cell = h('button', 'ac-cell') as HTMLButtonElement;
      cell.style.borderRadius = '7px';
      cell.addEventListener('click', () => clickCell(i));
      cell.addEventListener('contextmenu', (ev) => {
        ev.preventDefault();
        if (done || open.has(i)) return;
        if (flags.has(i)) flags.delete(i);
        else if (flags.size < MINES) flags.add(i);
        paint();
      });
      // Long press is the touch equivalent of a right click.
      let hold = 0;
      cell.addEventListener('pointerdown', () => {
        hold = window.setTimeout(() => {
          hold = 0;
          if (done || open.has(i)) return;
          if (flags.has(i)) flags.delete(i);
          else if (flags.size < MINES) flags.add(i);
          tone(300, 40);
          paint();
        }, 420);
      });
      const clearHold = (): void => {
        if (hold) window.clearTimeout(hold);
        hold = 0;
      };
      cell.addEventListener('pointerup', clearHold);
      cell.addEventListener('pointerleave', clearHold);
      cells.push(cell);
      board.append(cell);
    }

    life.frame(() => {
      if (started && !done) timeValue.textContent = String(Math.round((performance.now() - started) / 1000));
    });

    const note = h('p', 'ac-note', 'Neuf sur neuf, dix mines. Le premier clic est toujours sur.');
    const again = h('button', 'ac-ghost', 'Nouvelle grille');
    again.addEventListener('click', () => {
      mines = new Set();
      open = new Set();
      flags = new Set();
      started = 0;
      done = false;
      timeValue.textContent = '0';
      note.textContent = 'Neuf sur neuf, dix mines. Le premier clic est toujours sur.';
      paint();
    });

    paint();
    root.append(hud, board, note, again);
    return () => life.stop();
  },
};

// --------------------------------------------------------------------- simon

export const simonSays: GameDef = {
  id: 'simon-says',
  title: 'Simon',
  blurb: 'Repete la sequence de couleurs.',
  category: 'Réflexion',
  difficulty: 'Moyen',
  icon: '\u{1F3B5}',
  unit: 'tours',
  higherIsBetter: true,
  how: 'Regarde la sequence puis reproduis la. Elle s’allonge d’une couleur a chaque tour.',
  mount(root, ctx) {
    const life = new Lifecycle();
    /*
     * Dark when idle, BRIGHT when lit.
     *
     * The pads used to sit at full colour and merely go paler when they lit up,
     * which meant the signal had nowhere brighter to go: a saturated cyan pad
     * turning pale cyan for a quarter of a second is a change you have to be
     * looking for, and this is a game about spotting exactly that. Now an idle
     * pad is a dim version of itself and lighting up is a jump in brightness,
     * a glow and a small scale, which is the difference a memory game lives on.
     */
    const PADS = [
      { idle: '#0e5c6b', lit: '#67e8f9', glow: '#22d3ee', freq: 330 },
      { idle: '#75144a', lit: '#ff8fc4', glow: '#ff2d95', freq: 415 },
      { idle: '#1e6b3a', lit: '#86efac', glow: '#4ade80', freq: 495 },
      { idle: '#7c3a0a', lit: '#fdba74', glow: '#f97316', freq: 660 },
    ];

    /*
     * Difficulty is how long you get to look at each colour, and how quickly
     * they follow one another.
     *
     * Not the number of colours: four pads is what Simon IS, and a fifth would
     * be a different game. The sequence still grows by one every round at every
     * level, so what changes is how hard the sequence is to READ, which is the
     * part a difficulty should touch.
     */
    const LIT_MS = byLevel(ctx.level, 460, 320, 220);
    const GAP_MS = byLevel(ctx.level, 700, 520, 380);
    const GAP_FLOOR = byLevel(ctx.level, 380, 240, 170);

    const [roundBox, roundValue] = pill('Tour', '0');
    const hud = h('div', 'ac-hud');
    hud.append(roundBox);

    const board = h('div', 'ac-board');
    board.style.gridTemplateColumns = 'repeat(2, 1fr)';
    board.style.maxWidth = '360px';

    let sequence: number[] = [];
    let progress = 0;
    let accepting = false;
    /**
     * How far this run got, and whether it has been scored yet.
     *
     * The score belongs to the RUN, so it is submitted once: when you make a
     * mistake, or when you leave with a run in progress. Without the second
     * case, walking away at round ten would throw the whole run away, which is
     * the opposite mistake from paying for every round.
     */
    let reached = 0;
    let scored = false;
    const pads: HTMLButtonElement[] = [];

    const finish = (score: number): void => {
      if (scored) return;
      scored = true;
      if (score > 0) ctx.submit(score);
    };

    const dim = (i: number): void => {
      const pad = pads[i];
      pad.style.background = PADS[i].idle;
      pad.style.boxShadow = 'none';
      pad.style.transform = 'scale(1)';
    };

    const flash = (i: number, ms = LIT_MS): void => {
      const pad = pads[i];
      pad.style.background = PADS[i].lit;
      pad.style.boxShadow = `0 0 26px 6px ${PADS[i].glow}`;
      pad.style.transform = 'scale(1.06)';
      tone(PADS[i].freq, ms - 40, 'sine', 0.06);
      life.timeout(() => dim(i), ms - 60);
    };

    const playBack = (): void => {
      accepting = false;
      let t = 380;
      sequence.forEach((i) => {
        life.timeout(() => flash(i), t);
        // The gap shrinks as the sequence grows, or round twelve takes a minute
        // of watching before you may touch anything.
        t += Math.max(GAP_FLOOR, GAP_MS - sequence.length * 18);
      });
      life.timeout(() => {
        accepting = true;
        note.textContent = 'A toi';
      }, t);
    };

    const nextRound = (): void => {
      sequence.push(Math.floor(Math.random() * 4));
      progress = 0;
      roundValue.textContent = String(sequence.length);
      note.textContent = 'Regarde';
      playBack();
    };

    for (let i = 0; i < 4; i++) {
      const pad = h('button', 'ac-cell') as HTMLButtonElement;
      pad.style.background = PADS[i].idle;
      pad.style.border = 'none';
      pad.style.transition = 'background 90ms linear, box-shadow 90ms linear, transform 90ms ease';
      pad.addEventListener('click', () => {
        if (!accepting) return;
        flash(i, Math.min(200, LIT_MS));
        if (sequence[progress] === i) {
          progress++;
          if (progress === sequence.length) {
            // Round survived. Deliberately NOT scored here: the result is how
            // far you got in the WHOLE game, and submitting every round made
            // each sequence a fresh personal best, and so paid a ruby each.
            reached = sequence.length;
            accepting = false;
            life.timeout(nextRound, 620);
          }
          return;
        }
        accepting = false;
        note.textContent = `Raté. Séquence de ${sequence.length}.`;
        tone(140, 320, 'square');
        // The one submission of the run, at the moment it ends.
        finish(sequence.length - 1);
        for (let k = 0; k < pads.length; k++) dim(k);
        sequence = [];
        roundValue.textContent = '0';
        life.timeout(() => {
          start.hidden = false;
        }, 400);
      });
      pads.push(pad);
      board.append(pad);
    }

    const note = h('p', 'ac-note', 'Appuie sur commencer.');
    const start = h('button', 'ac-primary', 'Commencer');
    start.addEventListener('click', () => {
      start.hidden = true;
      sequence = [];
      reached = 0;
      scored = false;
      nextRound();
    });

    // Leaving with a run in progress still counts what you reached.
    life.add(() => finish(reached));

    root.append(hud, board, note, start);
    return () => life.stop();
  },
};
